import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  FEEDBACK_CATEGORY_LABELS,
  FEEDBACK_STATUS_LABELS,
  type FeedbackStatus,
  attachFeedbackAction,
  getAdminFeedback,
  lookupTerminologyByText,
  updateFeedbackStatus,
} from "@/api/feedback";
import { addReplaceAliasForTerminologyItem } from "@/api/terminology";
import { useToast } from "@/components/toast";

const STATUS_OPTIONS: FeedbackStatus[] = [
  "open",
  "in_progress",
  "resolved",
  "wontfix",
];

export default function AdminFeedbackDetail() {
  const { feedbackId = "" } = useParams<{ feedbackId: string }>();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [resolutionNote, setResolutionNote] = useState("");

  const query = useQuery({
    queryKey: ["admin-feedback", feedbackId],
    queryFn: async () => {
      const r = await getAdminFeedback(feedbackId);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    enabled: Boolean(feedbackId),
  });

  const item = query.data?.item;

  const lookupQuery = useQuery({
    queryKey: ["feedback-detail-lookup", feedbackId],
    queryFn: async () => {
      if (!item) return null;
      const lang =
        item.side === "target"
          ? (item.draft.target_lang as "ru" | "en") || "ru"
          : (item.draft.source_lang as "ru" | "en") || "en";
      const r = await lookupTerminologyByText({
        text: item.selected_text,
        target_lang: lang,
        draft_id: item.draft_id,
        limit: 5,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    enabled: Boolean(item),
    staleTime: 60_000,
  });

  const createReplaceAliasMutation = useMutation({
    mutationFn: async (params: {
      parentItemId: string;
      aliasText: string;
      canonicalText: string;
      exact: boolean;
    }) => {
      const r = await addReplaceAliasForTerminologyItem(params.parentItemId, {
        alias_text: params.aliasText,
        canonical_text: params.canonicalText,
        exact: params.exact,
      });
      if (!r.ok) throw new Error(r.error);
      const aliasId = r.data.alias_id;
      // Сразу attach action к feedback'у — закроет его в resolved
      const aa = await attachFeedbackAction(feedbackId, {
        action_kind: "replace_alias_created",
        action_item_id: aliasId,
        extra: { parent_item_id: params.parentItemId },
      });
      if (!aa.ok) throw new Error(aa.error);
      return r.data;
    },
    onSuccess: (data) => {
      toast(
        `Запрещённая форма создана: «${data.alias_text}» → «${data.canonical_text}»`,
        "success",
      );
      void qc.invalidateQueries({ queryKey: ["admin-feedback", feedbackId] });
      void qc.invalidateQueries({ queryKey: ["admin-feedback"] });
    },
    onError: (err: Error) =>
      toast(`Не создалось: ${err.message}`, "error"),
  });

  const statusMutation = useMutation({
    mutationFn: async (params: {
      status: FeedbackStatus;
      resolution_note?: string | null;
    }) => {
      const r = await updateFeedbackStatus(feedbackId, params);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast("Статус обновлён", "success");
      void qc.invalidateQueries({ queryKey: ["admin-feedback", feedbackId] });
      void qc.invalidateQueries({ queryKey: ["admin-feedback"] });
    },
    onError: (err: Error) => toast(`Не сохранено: ${err.message}`, "error"),
  });

  if (query.isLoading) return <div className="mt-empty">Загружаем…</div>;
  if (query.isError || !item) {
    return (
      <div className="mt-empty mt-empty--error">
        {(query.error as Error | undefined)?.message ?? "Не найдено"}
      </div>
    );
  }

  const sourceText = item.draft.source_text ?? "";
  const targetText = item.draft.target_text ?? "";

  return (
    <div style={{ padding: "var(--sp-3)", maxWidth: 1200 }}>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: "var(--sp-3)",
          flexWrap: "wrap",
        }}
      >
        <Link to="/admin/feedback" className="mt-btn mt-btn--sm mt-btn--ghost">
          ‹ К списку замечаний
        </Link>
        <span
          className={
            "mt-badge mt-badge--no-dot " +
            (item.status === "resolved"
              ? "mt-badge--success"
              : item.status === "wontfix"
                ? "mt-badge--neutral"
                : item.status === "orphaned"
                  ? "mt-badge--warning"
                  : "mt-badge--info")
          }
        >
          {FEEDBACK_STATUS_LABELS[item.status]}
        </span>
        <span className="mt-toolbar__sep" style={{ flex: 1 }} />
        <span className="mt-text-tight mt-muted">
          {item.created_at?.slice(0, 16).replace("T", " ")} · {item.author}
        </span>
        <Link
          to={`/translate/${encodeURIComponent(item.draft_id)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-btn mt-btn--sm mt-btn--ghost"
        >
          Открыть draft ↗
        </Link>
      </header>

      {/* Категории */}
      {item.categories.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          {item.categories.map((c) => (
            <span
              key={c}
              className="mt-badge mt-badge--info mt-badge--no-dot"
            >
              {FEEDBACK_CATEGORY_LABELS[c]}
            </span>
          ))}
        </div>
      )}

      {/* Контекст: оригинал + перевод бок-о-бок с подсветкой фрагмента */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "var(--sp-3)",
          marginBottom: "var(--sp-3)",
        }}
      >
        <ContextPane
          title="Оригинал"
          lang={item.draft.source_lang}
          text={sourceText}
          highlight={
            item.side === "source"
              ? {
                  selected: item.selected_text,
                  start: item.offset_start ?? null,
                  end: item.offset_end ?? null,
                }
              : null
          }
          pairedSelected={
            item.side === "target" ? item.paired_text ?? null : null
          }
        />
        <ContextPane
          title="Перевод"
          lang={item.draft.target_lang}
          text={targetText}
          highlight={
            item.side === "target"
              ? {
                  selected: item.selected_text,
                  start: item.offset_start ?? null,
                  end: item.offset_end ?? null,
                }
              : null
          }
          pairedSelected={
            item.side === "source" ? item.paired_text ?? null : null
          }
        />
      </div>

      {/* Comment */}
      {item.comment_text && (
        <section className="rf-section" style={{ marginBottom: "var(--sp-3)" }}>
          <div className="rf-section__head">
            <span>Комментарий reviewer'а</span>
          </div>
          <div className="rf-section__body" style={{ whiteSpace: "pre-wrap" }}>
            {item.comment_text}
          </div>
        </section>
      )}

      {/* Действия */}
      <section className="rf-section" style={{ marginBottom: "var(--sp-3)" }}>
        <div className="rf-section__head">
          <span>Действия</span>
        </div>
        <div
          className="rf-section__body"
          style={{ display: "flex", flexDirection: "column", gap: 12 }}
        >
          <textarea
            className="mt-input"
            placeholder="resolution note (опционально)"
            rows={2}
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
          />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                type="button"
                className={
                  "mt-btn mt-btn--sm " +
                  (item.status === s ? "mt-btn--secondary" : "mt-btn--ghost")
                }
                disabled={
                  statusMutation.isPending || item.status === s
                }
                onClick={() =>
                  statusMutation.mutate({
                    status: s,
                    resolution_note: resolutionNote.trim() || null,
                  })
                }
              >
                {s === "resolved"
                  ? "Решён"
                  : s === "wontfix"
                    ? "Won't fix"
                    : s === "in_progress"
                      ? "В работе"
                      : "Открыт"}
              </button>
            ))}
          </div>
          {item.linked_action && (
            <div
              className="mt-text-tight"
              style={{ textTransform: "none", letterSpacing: 0 }}
            >
              <strong>Связанное действие:</strong>{" "}
              <code>{(item.linked_action as { kind?: string }).kind}</code>
              {(item.linked_action as { item_id?: string }).item_id && (
                <>
                  {" → "}
                  <code>
                    {(item.linked_action as { item_id?: string }).item_id}
                  </code>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Похожие термины в словаре */}
      <section className="rf-section">
        <div className="rf-section__head">
          <span>Похожие термины в словаре</span>
          <span className="mt-toolbar__sep" style={{ flex: 1 }} />
          <span className="mt-text-tight mt-muted">
            {lookupQuery.data?.query_lemma
              ? `лемма: ${lookupQuery.data.query_lemma}`
              : ""}
          </span>
        </div>
        <div className="rf-section__body">
          {lookupQuery.isLoading ? (
            <div className="mt-empty">Ищем…</div>
          ) : !lookupQuery.data || lookupQuery.data.count === 0 ? (
            <div className="mt-empty">Совпадений в словаре не найдено.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {lookupQuery.data.items.map((c) => {
                const canonicalText = c.target_text || c.target_abbreviation || "";
                const isAbbrev = c.item_type === "abbreviation";
                const exactReplaceTarget = isAbbrev && c.target_abbreviation
                  ? c.target_abbreviation
                  : null;
                return (
                  <div
                    key={c.item_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: 8,
                      background: c.in_draft
                        ? "var(--blue-50, #f0f7ff)"
                        : "var(--panel)",
                      border: "1px solid var(--border-default, #d0d5dd)",
                      borderRadius: 6,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      className="mt-badge mt-badge--neutral mt-badge--no-dot"
                      style={{ fontSize: 10 }}
                    >
                      {c.item_type}
                    </span>
                    <Link
                      to={`/admin/terminology/${encodeURIComponent(c.item_id)}`}
                      target="_blank"
                      style={{ color: "var(--text)", textDecoration: "none" }}
                    >
                      {c.source_text} → <strong>{canonicalText}</strong>
                    </Link>
                    {c.in_draft && (
                      <span
                        className="mt-badge mt-badge--info mt-badge--no-dot"
                        style={{ fontSize: 9 }}
                      >
                        в этом переводе
                      </span>
                    )}
                    <span style={{ flex: 1 }} />
                    <span className="mt-text-tight mt-muted">
                      score {c.score.toFixed(2)}
                    </span>
                    {/* Action: запретить selected_text как replace-alias */}
                    {item.selected_text &&
                      item.selected_text.trim() !== canonicalText.trim() && (
                        <>
                          <button
                            type="button"
                            className="mt-btn mt-btn--sm mt-btn--ghost"
                            disabled={
                              createReplaceAliasMutation.isPending ||
                              !canonicalText
                            }
                            title={`Создать запрещённую форму: «${item.selected_text}» → «${canonicalText}»`}
                            onClick={() => {
                              if (
                                !window.confirm(
                                  `Создать запрещённую форму?\n\n«${item.selected_text}» → «${canonicalText}»\n\n` +
                                    "При следующем переводе resolver " +
                                    "будет автоматически заменять это.",
                                )
                              )
                                return;
                              createReplaceAliasMutation.mutate({
                                parentItemId: c.item_id,
                                aliasText: item.selected_text,
                                canonicalText,
                                exact: false,
                              });
                            }}
                          >
                            ✎ запретить → canonical
                          </button>
                          {exactReplaceTarget &&
                            item.selected_text.trim() !==
                              exactReplaceTarget.trim() && (
                              <button
                                type="button"
                                className="mt-btn mt-btn--sm mt-btn--ghost"
                                disabled={createReplaceAliasMutation.isPending}
                                title={`Точная подстановка: «${item.selected_text}» → «${exactReplaceTarget}»`}
                                onClick={() => {
                                  if (
                                    !window.confirm(
                                      `Создать exact-replace?\n\n«${item.selected_text}» → «${exactReplaceTarget}»\n\nБез морфологии (для аббревиатур).`,
                                    )
                                  )
                                    return;
                                  createReplaceAliasMutation.mutate({
                                    parentItemId: c.item_id,
                                    aliasText: item.selected_text,
                                    canonicalText: exactReplaceTarget,
                                    exact: true,
                                  });
                                }}
                              >
                                ✎ запретить → {exactReplaceTarget}
                              </button>
                            )}
                        </>
                      )}
                  </div>
                );
              })}
            </div>
          )}
          <div
            className="mt-text-tight mt-muted"
            style={{ marginTop: 8, textTransform: "none", letterSpacing: 0 }}
          >
            Подсказка: открой подходящую запись и добавь там запрещённую
            форму, либо запромоутируй candidate в ревью. Кнопки прямо
            из feedback'а — на следующем этапе (Stage E).
          </div>
        </div>
      </section>
    </div>
  );
}

interface ContextPaneProps {
  title: string;
  lang: string | null;
  text: string;
  highlight: {
    selected: string;
    start: number | null;
    end: number | null;
  } | null;
  pairedSelected: string | null;
}

function ContextPane({
  title,
  lang,
  text,
  highlight,
  pairedSelected,
}: ContextPaneProps) {
  return (
    <section className="rf-section">
      <div className="rf-section__head">
        <span>{title}</span>
        {lang && (
          <span className="mt-badge mt-badge--neutral mt-badge--no-dot">
            {lang}
          </span>
        )}
      </div>
      <div
        className="rf-section__body"
        style={{
          maxHeight: 360,
          overflow: "auto",
          whiteSpace: "pre-wrap",
        }}
      >
        {!text ? (
          <span className="mt-text-tight mt-muted">— нет текста —</span>
        ) : highlight ? (
          renderWithHighlight(text, highlight)
        ) : pairedSelected ? (
          renderWithSubstringMatch(text, pairedSelected)
        ) : (
          text
        )}
      </div>
    </section>
  );
}

function renderWithHighlight(
  text: string,
  highlight: { selected: string; start: number | null; end: number | null },
): React.ReactNode {
  const { selected, start, end } = highlight;
  // Если есть offset'ы — режем по ним, иначе ищем substring.
  let s = start ?? -1;
  let e = end ?? -1;
  if (s < 0 || e <= s || e > text.length || text.slice(s, e) !== selected) {
    const idx = text.indexOf(selected);
    if (idx < 0) return text;
    s = idx;
    e = idx + selected.length;
  }
  return (
    <>
      {text.slice(0, s)}
      <mark
        style={{
          background: "var(--yellow-200, #fde68a)",
          padding: "0 2px",
          borderRadius: 2,
        }}
      >
        {text.slice(s, e)}
      </mark>
      {text.slice(e)}
    </>
  );
}

function renderWithSubstringMatch(
  text: string,
  needle: string,
): React.ReactNode {
  if (!needle) return text;
  const idx = text.toLowerCase().indexOf(needle.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark
        style={{
          background: "var(--blue-100, #dbeafe)",
          padding: "0 2px",
          borderRadius: 2,
          opacity: 0.6,
        }}
      >
        {text.slice(idx, idx + needle.length)}
      </mark>
      {text.slice(idx + needle.length)}
    </>
  );
}
